/**
 * Decisions (`/decisions`, `/decisions/:decisionId`): the decision ledger on
 * the queue grammar. A header with the ledger's facts and its filters as
 * chips, the decisions grouped by what the reader owes them (Needs review,
 * Most relied on, History), and the record in the sheet. `/decisions/:id` is
 * the list with that record open; the record is fetched by id, never looked
 * up in the page, so a link pasted into a PR resolves months later.
 *
 * The page owns every read and one `refresh` counter that every write bumps,
 * so the rows, the facts, the open record and the rail's count all show the
 * post-action truth together. URL state is the filter state.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { CodeDecision, CodeDecisionScopes, CodeDecisionsResponse, CodeDecisionsSummary } from "@yagni/shared";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import {
  Btn,
  ChevronDownIcon,
  Group,
  Groups,
  ListHead,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MoreMenu,
  Row,
} from "../queue/index.js";
import { DockScope } from "../shell/DockScope.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { decisionsScope } from "../shell/yagniRailScopes.js";
import { DecisionSheet, NewDecisionSheet } from "./DecisionSheet.js";
import { decisionsApi, type DecisionsApi, type DecisionsQuery, type DecisionsView } from "./decisionsApi.js";
import { EMPTY_TITLE, EMPTY_WAYS, relativeDate, standingOf } from "./decisionCopy.js";
import { decisionStateLine, groupDecisions, kindTag, rowVerb, standingDot } from "./decisionsModel.js";
import { invalidateDecisionsSummary } from "./useDecisionsSummary.js";

/** How many rows a group reads at a time. */
export const BAND_PAGE = 50;

export interface DecisionsFilters {
  q: string;
  team: string;
  repo: string;
  workspaceOnly: boolean;
  workItem: string;
  author: string;
  /** A shared `?view=` link narrows the page to one group. */
  view: Band | "";
}

export function readFilters(params: URLSearchParams): DecisionsFilters {
  return {
    q: params.get("q") ?? "",
    team: params.get("team") ?? "",
    repo: params.get("repo") ?? "",
    workspaceOnly: params.get("scope") === "workspace",
    workItem: params.get("workItem") ?? "",
    author: params.get("author") ?? "",
    view: readView(params.get("view")),
  };
}

export function writeFilters(filters: DecisionsFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.team) params.set("team", filters.team);
  if (filters.repo) params.set("repo", filters.repo);
  if (filters.workspaceOnly) params.set("scope", "workspace");
  if (filters.workItem) params.set("workItem", filters.workItem);
  if (filters.author) params.set("author", filters.author);
  if (filters.view) params.set("view", filters.view);
  return params;
}

type Band = Exclude<DecisionsView, "all">;
const BANDS: Band[] = ["needs_review", "confirmed", "history"];
const BAND_LABEL: Record<Band, string> = { needs_review: "Needs review", confirmed: "Most relied on", history: "History" };

function readView(value: string | null): Band | "" {
  return value === "needs_review" || value === "confirmed" || value === "history" ? value : "";
}

function bandQuery(filters: DecisionsFilters, band: Band, limit: number): DecisionsQuery {
  return {
    q: filters.q || undefined,
    view: band,
    team: filters.team || undefined,
    repo: filters.repo || undefined,
    scope: filters.workspaceOnly ? "workspace" : undefined,
    workItem: filters.workItem || undefined,
    author: filters.author || undefined,
    sort: band === "confirmed" ? "cited" : undefined,
    limit,
  };
}

function scopeChipLabel(filters: DecisionsFilters, scopes: CodeDecisionScopes | null): string {
  if (filters.workspaceOnly) return "workspace-wide";
  if (filters.team) return scopes?.teams.find((t) => t.id === filters.team)?.name ?? "one Team";
  if (filters.repo) return filters.repo;
  return "every scope";
}

export interface DecisionsPageProps {
  api?: DecisionsApi;
  /** The signed-in person, for "Recorded by me"; defaults to the session's user. */
  currentUserId?: string;
}

export function DecisionsPage({ api = decisionsApi, currentUserId }: DecisionsPageProps) {
  const { decisionId } = useParams<{ decisionId?: string }>();
  const session = useAuthSession();
  const meId = currentUserId ?? session?.user.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const [draftQuery, setDraftQuery] = useState(filters.q);
  useEffect(() => setDraftQuery(filters.q), [filters.q]);

  const [refresh, setRefresh] = useState(0);
  const bump = useCallback(() => {
    setRefresh((n) => n + 1);
    invalidateDecisionsSummary();
  }, []);

  const [scopes, setScopes] = useState<CodeDecisionScopes | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .scopes()
      .then((loaded) => {
        if (!cancelled) setScopes(loaded);
      })
      .catch((err: unknown) => console.warn("[decisions] scope picker unavailable", err));
    return () => {
      cancelled = true;
    };
  }, [api]);

  const [summary, setSummary] = useState<CodeDecisionsSummary | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .summary({
        team: filters.team || undefined,
        repo: filters.repo || undefined,
        workspaceOnly: filters.workspaceOnly || undefined,
      })
      .then((loaded) => {
        if (!cancelled) setSummary(loaded);
      })
      .catch((err: unknown) => {
        console.warn("[decisions] summary unavailable", err);
        if (!cancelled) setSummary(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, filters.team, filters.repo, filters.workspaceOnly, refresh]);

  const [limits, setLimits] = useState<Record<Band, number>>({ needs_review: BAND_PAGE, confirmed: BAND_PAGE, history: BAND_PAGE });
  const [pages, setPages] = useState<Partial<Record<Band, CodeDecisionsResponse>>>({});
  const [listState, setListState] = useState<"loading" | "ready" | "error">("loading");
  const [listError, setListError] = useState<string | null>(null);
  const [retryList, setRetryList] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setListState((was) => (was === "ready" ? "ready" : "loading"));
    Promise.all(BANDS.map((band) => api.list(bandQuery(filters, band, limits[band]))))
      .then(([needs, confirmed, history]) => {
        if (cancelled) return;
        setPages({ needs_review: needs, confirmed, history });
        setListState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setListError(err instanceof Error ? err.message : "Failed to load decisions");
        setListState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [api, filters, limits, refresh, retryList]);

  const [detail, setDetail] = useState<CodeDecision | null>(null);
  const [detailState, setDetailState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [detailError, setDetailError] = useState<string | null>(null);
  const [retryDetail, setRetryDetail] = useState(0);
  const shownId = useRef<string | null>(null);
  useEffect(() => {
    if (!decisionId) {
      shownId.current = null;
      setDetail(null);
      setDetailState("loading");
      return;
    }
    let cancelled = false;
    // A re-read after a write keeps the record on screen; a new id reads fresh.
    if (shownId.current !== decisionId) setDetailState("loading");
    shownId.current = decisionId;
    setDetailError(null);
    api
      .get(decisionId)
      .then((loaded) => {
        if (cancelled) return;
        setDetail(loaded);
        setDetailState(loaded ? "ready" : "missing");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setDetailError(err instanceof Error ? err.message : "Failed to load this decision");
        setDetailState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [api, decisionId, retryDetail, refresh]);

  const qs = searchParams.toString();
  const listHref = qs ? `/decisions?${qs}` : "/decisions";
  const hrefFor = useCallback((id: string) => `/decisions/${encodeURIComponent(id)}${qs ? `?${qs}` : ""}`, [qs]);
  const setFilters = useCallback(
    (patch: Partial<DecisionsFilters>) => {
      setLimits({ needs_review: BAND_PAGE, confirmed: BAND_PAGE, history: BAND_PAGE });
      const next = writeFilters({ ...filters, ...patch }).toString();
      const path = decisionId ? `/decisions/${encodeURIComponent(decisionId)}` : "/decisions";
      navigate(next ? `${path}?${next}` : path, { replace: true });
    },
    [decisionId, filters, navigate],
  );

  const [recording, setRecording] = useState(false);
  const openRecord = (id: string) => {
    setRecording(false);
    navigate(hrefFor(id));
  };

  const sheet = recording ? (
    <NewDecisionSheet
      api={api}
      scopes={scopes}
      defaultRepo={filters.repo || undefined}
      onRecorded={(id) => {
        bump();
        openRecord(id);
      }}
      onOpenExisting={openRecord}
      onCancel={() => setRecording(false)}
    />
  ) : decisionId ? (
    <DecisionSheet
      api={api}
      decision={detail}
      state={detailState}
      error={detailError}
      onRetry={() => setRetryDetail((n) => n + 1)}
      onChanged={bump}
      hrefFor={hrefFor}
      onClose={() => navigate(listHref)}
    />
  ) : null;
  useOwnedSheet(sheet, () => {
    if (recording) setRecording(false);
    else if (decisionId) navigate(listHref);
  });

  const all = [
    ...(pages.needs_review?.items ?? []),
    ...(pages.confirmed?.items ?? []),
    ...(pages.history?.items ?? []),
  ];
  const groups = groupDecisions(all);
  const totals = {
    needs: pages.needs_review?.total ?? groups.needs.length,
    relied: pages.confirmed?.total ?? groups.relied.length,
    history: pages.history?.total ?? groups.history.length,
  };
  const shows = (band: Band) => !filters.view || filters.view === band;
  const filtered = Boolean(filters.view || filters.q || filters.team || filters.repo || filters.workspaceOnly || filters.workItem || filters.author);
  const ledgerEmpty = listState === "ready" && !filtered && totals.needs + totals.relied + totals.history === 0;

  const row = (decision: CodeDecision) => {
    const dot = standingDot(standingOf(decision));
    const tag = kindTag(decision.kind);
    return (
      <Row
        key={decision.id}
        glyph={<span className={dot.mode ? `sdot ${dot.mode}` : "sdot"} style={{ "--c": dot.color } as CSSProperties} aria-hidden />}
        rowKey={decision.repoFullName ? decision.repoFullName.split("/").pop() : "workspace"}
        title={decision.decision}
        tag={tag ? { label: tag } : undefined}
        state={decisionStateLine(decision)}
        when={relativeDate(decision.createdAt)}
        verb={rowVerb(decision)}
        sel={!recording && decision.id === decisionId}
        onOpen={() => openRecord(decision.id)}
      />
    );
  };
  const moreRows = (band: Band, shown: number, total: number) =>
    total > shown ? (
      <button
        type="button"
        className="more-rows"
        onClick={() => setLimits((current) => ({ ...current, [band]: current[band] + BAND_PAGE }))}
      >
        Show {Math.min(BAND_PAGE, total - shown)} more of {total - shown}
      </button>
    ) : null;

  const recorded = summary ? summary.active + summary.retired : null;
  const chips = (
    <>
      <MoreMenu
        label="Scope"
        align="left"
        trigger={({ open, toggle }) => (
          <button type="button" className="chip" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
            Scope <b>{scopeChipLabel(filters, scopes)}</b>
            <ChevronDownIcon />
          </button>
        )}
      >
        <MenuItem on={!filters.team && !filters.repo && !filters.workspaceOnly} onSelect={() => setFilters({ team: "", repo: "", workspaceOnly: false })}>
          Every scope
        </MenuItem>
        <MenuItem on={filters.workspaceOnly} onSelect={() => setFilters({ team: "", repo: "", workspaceOnly: true })}>
          Workspace-wide
        </MenuItem>
        {(scopes?.teams.length ?? 0) > 0 && (
          <>
            <MenuSeparator />
            <MenuLabel>Teams</MenuLabel>
            {scopes!.teams.map((team) => (
              <MenuItem key={team.id} on={filters.team === team.id} onSelect={() => setFilters({ team: team.id, repo: "", workspaceOnly: false })}>
                {team.name}
              </MenuItem>
            ))}
          </>
        )}
        {(scopes?.repos.length ?? 0) > 0 && (
          <>
            <MenuSeparator />
            <MenuLabel>Repositories</MenuLabel>
            {scopes!.repos.map((repo) => (
              <MenuItem key={repo} on={filters.repo === repo} onSelect={() => setFilters({ team: "", repo, workspaceOnly: false })}>
                {repo}
              </MenuItem>
            ))}
          </>
        )}
      </MoreMenu>
      {meId && (
        <MoreMenu
          label="Recorded by"
          align="left"
          trigger={({ open, toggle }) => (
            <button type="button" className="chip" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
              Recorded by <b>{filters.author ? "me" : "anyone"}</b>
              <ChevronDownIcon />
            </button>
          )}
        >
          <MenuItem on={!filters.author} onSelect={() => setFilters({ author: "" })}>
            Anyone
          </MenuItem>
          <MenuItem on={Boolean(filters.author)} onSelect={() => setFilters({ author: meId })}>
            Me
          </MenuItem>
        </MoreMenu>
      )}
      <form
        className="chip search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setFilters({ q: draftQuery.trim() });
        }}
      >
        <input
          aria-label="Search decisions"
          placeholder="Search"
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
        />
        {filters.q && (
          <button type="button" className="x" aria-label="Clear search" onClick={() => setFilters({ q: "" })}>
            ×
          </button>
        )}
      </form>
      {filters.view && (
        <span className="chip on">
          Only <b>{BAND_LABEL[filters.view]}</b>
          <button type="button" className="x" aria-label="Show every group" onClick={() => setFilters({ view: "" })}>
            ×
          </button>
        </span>
      )}
      {filters.workItem && (
        <span className="chip">
          One ticket
          <button type="button" className="x" aria-label="Show every ticket" onClick={() => setFilters({ workItem: "" })}>
            ×
          </button>
        </span>
      )}
    </>
  );

  return (
    <div className="wsurf" data-testid="decisions-surface">
      <ListHead
        title="Decisions"
        sub={[
          "What the Teams and the people here have settled, read by every agent before it plans or builds",
          recorded == null ? "" : `${recorded} recorded · ${summary!.repos} ${summary!.repos === 1 ? "repository" : "repositories"}`,
        ].filter(Boolean)}
        acts={
          <Btn variant="primary" size="sm" onClick={() => setRecording(true)} data-testid="record-decision-open">
            Record a decision
          </Btn>
        }
        chips={chips}
        picked={summary && summary.recent > 0 ? <><b>{summary.recent} recorded</b> this week</> : undefined}
      />
      <Groups>
        {listState === "error" && (
          <div className="qnote err" role="alert" data-testid="decisions-list-error">
            <span>{listError ?? "Failed to load decisions."}</span>
            <span className="acts">
              <Btn size="sm" variant="secondary" onClick={() => setRetryList((n) => n + 1)}>
                Try again
              </Btn>
            </span>
          </div>
        )}
        {ledgerEmpty ? (
          <div className="empty dashed" data-testid="decisions-empty">
            <h3>{EMPTY_TITLE}</h3>
            {EMPTY_WAYS.map((way) => (
              <p key={way.title} style={{ marginTop: 8 }}>
                <b>{way.title}.</b> {way.detail}
              </p>
            ))}
          </div>
        ) : (
          <>
            {shows("needs_review") && <Group
              label="Needs review"
              tone="needs"
              count={totals.needs}
              empty={listState === "loading" ? "Reading the ledger." : "Nothing waits for review."}
            >
              {groups.needs.map(row)}
            </Group>}
            {shows("needs_review") && moreRowsIn(moreRows("needs_review", groups.needs.length, totals.needs))}
            {shows("confirmed") && (
              <Group label="Most relied on" plain count={totals.relied}>
                {groups.relied.map(row)}
              </Group>
            )}
            {shows("confirmed") && moreRowsIn(moreRows("confirmed", groups.relied.length, totals.relied))}
            {shows("history") && (
              <Group label="History" tone="done" count={totals.history}>
                {groups.history.map(row)}
              </Group>
            )}
            {shows("history") && moreRowsIn(moreRows("history", groups.history.length, totals.history))}
            {filtered && listState === "ready" && totals.needs + totals.relied + totals.history === 0 && (
              <div className="empty dashed tight">
                <p>No decisions match these filters.</p>
              </div>
            )}
          </>
        )}
      </Groups>
      <DockScope scope={decisionsScope(decisionId && detail ? detail : null)} />
    </div>
  );
}

function moreRowsIn(button: ReactNode) {
  return button ? <div className="group" style={{ marginTop: -12 }}>{button}</div> : null;
}
